import { Users, ListChecks, TrendingUp, Target, CalendarCheck, KeyRound } from "lucide-react";
import { Reveal, Overline, SectionTitle } from "./Reveal";

const Card = ({ testId, className = "", children, delay = 0 }) => (
  <Reveal delay={delay} className={className}>
    <div data-testid={testId} className="group relative h-full overflow-hidden rounded-2xl border border-white/[0.08] bg-[#16181f] p-6 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-[#ff6a3d]/40 md:p-8">
      {children}
    </div>
  </Reveal>
);

const Head = ({ icon: Icon, title, text }) => (
  <div className="relative">
    <Icon className="h-6 w-6 text-[#ff6a3d]" />
    <h3 className="mt-5 font-display text-3xl font-extrabold uppercase leading-none">{title}</h3>
    <p className="mt-3 max-w-sm text-sm leading-relaxed text-[#b3b8c1]">{text}</p>
  </div>
);

const Streak = () => (
  <div className="mt-6 grid grid-cols-7 gap-1.5">
    {Array.from({ length: 21 }).map((_, i) => (
      <span key={i} className={`aspect-square rounded-[4px] ${[2, 5, 9, 13, 16].includes(i) ? "bg-white/[0.06]" : "bg-grad"}`} style={{ opacity: 0.45 + (i / 21) * 0.55 }} />
    ))}
  </div>
);

const SetLog = () => (
  <div className="mt-6 space-y-2 font-mono-x text-xs">
    {[["Set 1", "100 kg × 6", "✓ Done"], ["Rest", "next set in", "1:30"]].map(([e, s, w]) => (
      <div key={e} className="flex justify-between rounded-lg bg-[#1f222a] px-4 py-3 transition-transform duration-500 group-hover:translate-x-1"><span>{e}</span><span className="text-[#9ca3af]">{s}</span><span className="text-[#ff8a3d]">{w}</span></div>
    ))}
  </div>
);

const Bars = () => (
  <div className="mt-6 flex h-24 items-end gap-2">
    {[30, 38, 35, 50, 58, 55, 70, 82, 95].map((h, i) => (
      <span key={i} className="flex-1 origin-bottom rounded-t-md bg-grad transition-transform duration-700 group-hover:scale-y-110" style={{ height: `${h}%`, opacity: 0.4 + i * 0.07 }} />
    ))}
  </div>
);

export const Features = () => (
  <section id="features" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-36">
    <div className="mb-14 grid gap-6 lg:grid-cols-12 lg:items-end">
      <Reveal className="lg:col-span-7"><Overline>What Spotter does</Overline><SectionTitle className="mt-4">Every rep counts.<br /><span className="text-grad">So we count them.</span></SectionTitle></Reveal>
      <Reveal delay={0.1} className="lg:col-span-5"><p className="text-base leading-relaxed text-[#b3b8c1] md:text-lg">Record the work, see the results and build the habit. Everything you need to train smarter — nothing you don't.</p></Reveal>
    </div>
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-12 lg:grid-rows-[auto_auto_auto]">
      <Card testId="bento-feature-card-train-together" className="md:col-span-2 lg:col-span-7 lg:row-span-2">
        <img src="/images/partners.jpg" alt="Two gym partners training on a bench press" className="photo-treat absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e0f12] via-[#0e0f12]/70 to-transparent" />
        <div className="relative flex min-h-[380px] flex-col justify-end lg:min-h-[520px]">
          <Head icon={Users} title="Train together" text="One Spotter = one shared workout plan and meal plan for you and your gym buddy. Everyone gets their own weights and videos for every exercise." />
        </div>
      </Card>
      <Card testId="bento-feature-card-tracker" className="lg:col-span-5" delay={0.08}>
        <Head icon={ListChecks} title="Track workouts" text="Log every set: weight and reps, tick it off, and Spotter times your rest between sets and machines." />
        <SetLog />
      </Card>
      <Card testId="bento-feature-card-progress" className="lg:col-span-5" delay={0.12}>
        <Head icon={TrendingUp} title="Track progress" text="Weekly weigh-ins with a trend chart, progress photos that stay on your phone, and smart weights that go up when it's too easy." />
        <Bars />
      </Card>
      <Card testId="bento-feature-card-goals" className="lg:col-span-4">
        <Head icon={Target} title="Set goals" text="Lose, maintain or build muscle. Spotter works out your daily calories and updates them as your weight changes." />
      </Card>
      <Card testId="bento-feature-card-consistency" className="lg:col-span-4" delay={0.08}>
        <Head icon={CalendarCheck} title="Build consistency" text="A weekly plan from Monday to Sunday, about an hour a day. Change the body part any day; Spotter builds a balanced workout." />
        <Streak />
      </Card>
      <Card testId="bento-feature-card-accountable" className="md:col-span-2 lg:col-span-4" delay={0.12}>
        <Head icon={KeyRound} title="Stay accountable" text="Changes sync to your buddy's phone in about 30 seconds. No email, no password, no ads and no tracking. Just a code." />
      </Card>
    </div>
  </section>
);
